import React from 'react';
import { Box, TextField } from '@mui/material';
import { useTranslation } from 'services/localization';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import BackdropDialog from 'common/components/BackdropDialog';
import { ManualPaymentMethodDetails } from 'common/modules/payments/types';
import { newFirestoreId } from 'common/utils/dataMigrations';
import { getTranslation } from 'common/modules/translations';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';

interface Props {
	isOpen: boolean;
	onClose: (value: boolean) => void;
	onEdit: (value: ManualPaymentMethodDetails) => Promise<void>;
	onCreate: (value: ManualPaymentMethodDetails) => Promise<void>;
	paymentMethod?: ManualPaymentMethodDetails;
}

type FormValues = {
	label: string;
	description: string;
};

const AddManualPaymentMethodDialog = (props: Props) => {
	const { isOpen, onClose, onEdit, onCreate, paymentMethod } = props;
	const lang = useSelector(LanguageSelectors.language);
	const defaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const { t } = useTranslation();

	const defaultValues: FormValues = {
		label: paymentMethod ? getTranslation(paymentMethod.label, lang) : '',
		description: paymentMethod?.description ? getTranslation(paymentMethod.description, lang) : '',
	};

	const manualMethodsForm = useForm<FormValues>({
		defaultValues,
		mode: 'onChange',
	});

	const { isDirty, isValid, isSubmitting } = manualMethodsForm.formState;

	const onSubmit = (data: FormValues) => {
		if (paymentMethod) {
			const editedPaymentMethodObject: ManualPaymentMethodDetails = {
				...paymentMethod,
				label: { def: data.label, [defaultLanguage]: data.label },
				description: { def: data.description, [defaultLanguage]: data.description },
			};
			onEdit(editedPaymentMethodObject);
		} else {
			const newPaymentMethodObject = {
				id: newFirestoreId(),
				label: { def: data.label },
				description: { def: data.description },
			};
			onCreate(newPaymentMethodObject);
		}
	};

	return (
		<BackdropDialog
			isOpen={isOpen}
			onClose={() => onClose(false)}
			label={
				!!paymentMethod
					? t('account.payments.editPaymentMethod', 'Edit payment method')
					: t('shop.addPaymentMethod')
			}
			width="sm"
			body={
				<>
					<Box mb={3}>
						<Controller
							control={manualMethodsForm.control}
							name="label"
							rules={{
								validate: (value) => {
									return !!value.length;
								},
							}}
							render={(renderProps) => (
								<TextField
									label={t('common:form.name')}
									placeholder={t('account.payments.dialogLabelPlaceholder', 'E.g. Pay via invoice')}
									InputLabelProps={{ shrink: true }}
									helperText={t(
										'account.payments.dialogLabelHelperText',
										'Shown to customers at checkout',
									)}
									fullWidth
									value={renderProps.field.value}
									onChange={(e) => renderProps.field.onChange(e.target.value)}
									required
									inputProps={{
										maxLength: 100,
									}}
								/>
							)}
						/>
					</Box>
					<Box>
						<Controller
							control={manualMethodsForm.control}
							name="description"
							render={(renderProps) => (
								<TextField
									minRows={3}
									multiline
									fullWidth
									helperText={t(
										'account.payments.dialogDescriptionHelperText',
										'Displays to customers at checkout and in the confirmation email',
									)}
									label={t('common:form.additionalDetails', 'Additional details')}
									placeholder={t(
										'account.payments.dialogDescriptionPlaceholder',
										'Additional details',
									)}
									InputLabelProps={{ shrink: true }}
									value={renderProps.field.value}
									onChange={(e) => renderProps.field.onChange(e.target.value)}
								/>
							)}
						/>
					</Box>
				</>
			}
			confirm={{
				label: t('common:actions.save'),
				callback: manualMethodsForm.handleSubmit(onSubmit),
				closeAfterDone: true,
				disabled: !isDirty || !isValid || isSubmitting,
				loading: isSubmitting,
			}}
			cancel={{
				label: t('common:actions.cancel'),
				autoFocus: false,
			}}
		/>
	);
};

export default AddManualPaymentMethodDialog;
